"use client";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogOverlay, DialogTitle } from "@radix-ui/react-dialog";
import { useState } from "react";

interface VideoIframeProps {
  videoUrl: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export const VideoIframe = ({ videoUrl, isOpen, onClose }: VideoIframeProps) => {
  const embedUrl = videoUrl ? getEmbedUrl(videoUrl) : null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogOverlay 
        className="fixed inset-0 bg-black/90 cursor-pointer z-700" 
      />
      <DialogContent 
        className="fixed dark inset-0 !max-w-none w-full h-full p-0 border-none flex items-center justify-center z-701"
        onPointerDownOutside={onClose}
        onEscapeKeyDown={onClose}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }}
      >
        <DialogTitle className="hidden">{videoUrl}</DialogTitle>
          {embedUrl && (
            <>
              <Button
                onClick={onClose}
                size="iconLg"
                variant="outline"
                className="absolute top-4 right-4 z-[102] p-2"
                aria-label="Close video"
              >
                <span className="material-symbols-outlined text-white">close</span>
              </Button>
              <div className="relative w-full max-w-[1180px] aspect-video pointer-events-auto">
                <iframe
                  src={embedUrl}
                  className="absolute inset-0 w-full h-full rounded-lg"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </>
          )}
      </DialogContent>
    </Dialog>
  );
};

const getVideoPlatform = (url: string): 'youtube' | 'vimeo' | null => {
  if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
  if (url.includes('vimeo.com')) return 'vimeo';
  return null;
};

const getVideoId = (url: string, platform: 'youtube' | 'vimeo'): string | null => {
  if (platform === 'youtube') {
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  }
  if (platform === 'vimeo') {
    const match = url.match(/(?:vimeo\.com\/)(\d+)/);
    return match ? match[1] : null;
  }
  return null;
};

const getEmbedUrl = (url: string): string | null => {
  const platform = getVideoPlatform(url);
  if (!platform) return null;

  const videoId = getVideoId(url, platform);
  if (!videoId) return null;

  if (platform === 'youtube') {
    return `https://www.youtube.com/embed/${videoId}`;
  }
  if (platform === 'vimeo') {
    return `https://player.vimeo.com/video/${videoId}`;
  }
  return null;
};

// Helper hook for managing video state
export const useVideo = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<string | null>(null);

  const getVideoThumbnail = (url: string): string | null => {
    const platform = getVideoPlatform(url);
    if (!platform) return null;

    const videoId = getVideoId(url, platform);
    if (!videoId) return null;

    if (platform === 'youtube') {
      return `https://img.youtube.com/vi/${videoId}/0.jpg`;
    }
    if (platform === 'vimeo') {
      return `https://vumbnail.com/${videoId}.jpg`;
    }
    return null;
  };

  const handleVideoClick = (videoUrl: string) => {
    setIsVideoOpen(true);
    setCurrentVideo(videoUrl);
  };

  const handleCloseVideo = () => {
    setIsVideoOpen(false);
    setCurrentVideo(null);
  };

  return {
    isVideoOpen,
    currentVideo,
    getVideoThumbnail,
    handleVideoClick,
    handleCloseVideo,
  };
};